import type { CallMediaUrls } from "@repo/app-types/api/interview";
import {
  LoadingState,
  type TranscriptFragment,
} from "@repo/app-types/client/client";
import {
  allTranslationLanguages,
  type InterviewLanguage,
  LANGUAGE_DISPLAY_NAMES,
} from "@repo/app-types/constants/languages";
import type { Activity } from "@repo/app-types/entities/activity";
import type {
  CompletedInterviewCallMetadata,
  InterviewCallMetadata,
  InterviewInsights,
} from "@repo/app-types/entities/interview";
import { getInterviewSectionTitle } from "@repo/app-types/entities/interviewSection/definition";
import type { AdminApiInterviewSectionResult } from "@repo/app-types/entities/interviewSection/result";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { DropdownMenu } from "@repo/ui/Dropdown/dropdownMenu";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { TextSkeleton } from "@repo/ui/Skeletons/skeletons";
import { IconMessageCircle, IconPhone } from "@tabler/icons-react";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { MenuTrigger } from "react-aria-components";
import { useLocation } from "react-router";
import { twMerge } from "tailwind-merge";
import { TranscriptFragmentText } from "../../insights/transcriptFragmentText.js";
import { UnansweredQuestionsBanner } from "./unansweredQuestionsBanner.js";

const STICKY_HEADER_HEIGHT = 80;

interface InterviewTranscriptProps {
  loadingState: LoadingState;
  interviewSectionResults: readonly AdminApiInterviewSectionResult[];
  transcriptFragments: TranscriptFragment[];
  highlightedTranscriptFragment: string | null;
  onClickShare: (fragment: TranscriptFragment) => void;
  onHighlightFragment: (fragmentId: string | null) => void;
  calls: CompletedInterviewCallMetadata[];
  callMediaUrls: CallMediaUrls[] | undefined;
  interviewInsights: InterviewInsights | null;
  onClickTranslate?: (targetLanguage: InterviewLanguage) => void;
  jumpToFragmentByText: (text: string) => void;
  outreachActivities: readonly Activity[];
}

const speedOptions = [
  { label: "1x speed", value: 1 },
  { label: "1.25x speed", value: 1.25 },
  { label: "1.5x speed", value: 1.5 },
  { label: "2x speed", value: 2 },
];

interface BaseTimelineEvent {
  type: string;
  timestamp: number;
  data: unknown;
}

interface TranscriptTimelineEvent extends BaseTimelineEvent {
  type: "transcript";
  data: TranscriptFragment;
}

interface CallTimelineEvent extends BaseTimelineEvent {
  type: "call_start" | "call_end";
  data: CompletedInterviewCallMetadata;
}

interface InterviewSectionTimelineEvent extends BaseTimelineEvent {
  type: "section_start";
  data: AdminApiInterviewSectionResult;
}

interface SmsTimelineEvent extends BaseTimelineEvent {
  type: "sms_reminder";
  data: Activity;
  isFirstSms: boolean;
}

type TimelineEvent =
  | TranscriptTimelineEvent
  | CallTimelineEvent
  | InterviewSectionTimelineEvent
  | SmsTimelineEvent;

const timelineEventTypeOrder: readonly TimelineEvent["type"][] = Object.freeze([
  "sms_reminder",
  "section_start",
  "call_start",
  "transcript",
  "call_end",
]);

// Create translation dropdown items
const translateDropdownItems = allTranslationLanguages.map((language) => ({
  label: LANGUAGE_DISPLAY_NAMES[language],
  value: language,
}));

export const InterviewTranscript: FC<InterviewTranscriptProps> = ({
  loadingState,
  interviewSectionResults,
  transcriptFragments,
  highlightedTranscriptFragment,
  onClickShare,
  onHighlightFragment,
  calls,
  callMediaUrls,
  onClickTranslate,
  interviewInsights,
  jumpToFragmentByText,
  outreachActivities,
}) => {
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
  const [audioOnlyCallIds, setAudioOnlyCallIds] = useState<Set<string>>(
    new Set(),
  );
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const callIdToAudioRef = useRef<{ [key: string]: HTMLAudioElement | null }>(
    {},
  );
  const transcriptFragmentRefs = useRef<{ [key: string]: HTMLDivElement }>({});
  const location = useLocation();

  const [selectedLanguage, setSelectedLanguage] =
    useState<InterviewLanguage | null>(null);

  const jumpToFragmentById = (fragmentId: string) => {
    if (transcriptFragmentRefs.current[fragmentId]) {
      onHighlightFragment(fragmentId);
      setTimeout(() => onHighlightFragment(null), 5000);
      transcriptFragmentRefs.current[fragmentId].scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  // Jump to fragment if fragment ID is included as a query param
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const fragmentId = queryParams.get("f");

    if (fragmentId && transcriptFragments.length > 0) {
      jumpToFragmentById(fragmentId);
    }
  }, [location.search, transcriptFragments]);

  // Jump to fragment when the highlighted fragment ID changes
  useEffect(() => {
    if (highlightedTranscriptFragment) {
      jumpToFragmentById(highlightedTranscriptFragment);
    }
  }, [highlightedTranscriptFragment]);

  // Update playback speed on audio elements when it changes
  useEffect(() => {
    Object.values(callIdToAudioRef.current).forEach((audio) => {
      if (audio) {
        audio.playbackRate = playbackSpeed;
      }
    });
  }, [playbackSpeed]);

  const handleAudioError = (url: string) => {
    setFailedUrls((prev) => new Set(prev).add(url));
  };

  const getVideoUrl = (call: InterviewCallMetadata) => {
    return callMediaUrls?.find((c) => c.id === call.id)?.video_url;
  };

  const getAudioRecordingUrl = (call: InterviewCallMetadata) => {
    return callMediaUrls?.find((c) => c.id === call.id)?.audio_url;
  };

  // Add a ref callback to set the initial playback speed when audio elements are created
  const setAudioRef = (id: string, element: HTMLAudioElement | null) => {
    callIdToAudioRef.current[id] = element;
    if (element) {
      element.playbackRate = playbackSpeed;
    }
  };

  const toggleAudioOnly = (callId: string) => {
    setAudioOnlyCallIds((prev) => {
      const next = new Set(prev);
      if (next.has(callId)) {
        next.delete(callId);
      } else {
        next.add(callId);
      }
      return next;
    });
  };

  // Handles user clicking on a transcript fragment to play the corresponding audio
  const playAudioAtFragment = (fragment: TranscriptFragment) => {
    const fragmentTime = new Date(fragment.start_time).getTime();

    // Find the corresponding call by checking if fragment time falls between call start/end
    const matchingCall = calls.find((call) => {
      const startTime = new Date(call.start_time).getTime();
      const endTime = new Date(call.end_time).getTime();
      return fragmentTime >= startTime && fragmentTime <= endTime;
    });

    if (matchingCall) {
      const audio = callIdToAudioRef.current[matchingCall.id];
      if (audio) {
        // Calculate relative position in seconds
        const startTime = new Date(matchingCall.start_time).getTime();
        const relativePosition = (fragmentTime - startTime) / 1000; // Convert to seconds

        audio.currentTime = relativePosition;
        audio.play();
      }
    }
  };

  const timelineEvents = useMemo(() => {
    // Create chronological timeline of transcript fragments and call events
    const events: TimelineEvent[] = transcriptFragments.map((fragment) => ({
      type: "transcript",
      timestamp: new Date(fragment.start_time).getTime(),
      data: fragment,
    }));

    // Add call events
    for (const call of calls) {
      if (call.start_time) {
        events.push({
          type: "call_start",
          timestamp: new Date(call.start_time).getTime(),
          data: call,
        });
      }
      if (call.end_time) {
        events.push({
          type: "call_end",
          timestamp: new Date(call.end_time).getTime(),
          data: call,
        });
      }
    }

    // Add SMS reminder events
    const sortedSmsActivities = outreachActivities
      .slice()
      .sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );

    sortedSmsActivities.forEach((activity, index) => {
      events.push({
        type: "sms_reminder",
        timestamp: new Date(activity.created_at).getTime(),
        data: activity,
        isFirstSms: index === 0,
      });
    });

    // Sort by timestamp
    events.sort((a, b) => {
      if (a.timestamp !== b.timestamp) {
        return a.timestamp - b.timestamp;
      }

      // This makes it so "call started" header ends up before the first transcript fragment, even if those timestamps are the same
      const aTypeRank = timelineEventTypeOrder.indexOf(a.type);
      const bTypeRank = timelineEventTypeOrder.indexOf(b.type);
      if (aTypeRank !== -1 && bTypeRank !== -1 && aTypeRank !== bTypeRank) {
        return aTypeRank - bTypeRank;
      }

      return 0;
    });

    // Insert interview section markers
    let markedSectionResultId: string | undefined;
    for (let i = 0; i < events.length; i++) {
      // Determine the section for the current event
      const event = events[i];
      if (!event) {
        continue;
      }
      const sectionResultId =
        event.type === "call_start" || event.type === "transcript"
          ? event.data.interview_section_result_id
          : undefined;

      // Check if the section has changed since the last marker
      if (!sectionResultId || sectionResultId === markedSectionResultId) {
        continue;
      }
      markedSectionResultId = sectionResultId;

      // Insert a marker for the new section
      const interviewSectionResult = interviewSectionResults.find(
        (section) => section.id === sectionResultId,
      );
      if (interviewSectionResult) {
        events.splice(i, 0, {
          type: "section_start",
          timestamp: event.timestamp,
          data: interviewSectionResult,
        });
        i++; // Increment after splice to account for the newly inserted element
      }
    }

    return events;
  }, [interviewSectionResults, transcriptFragments, calls, outreachActivities]);

  const hasRecordings = calls.some(
    (call) => getVideoUrl(call) || getAudioRecordingUrl(call),
  );

  if (loadingState === LoadingState.LOADING) {
    return (
      <div className="mt-5 flex w-full flex-col space-y-5">
        <TextSkeleton />
        <TextSkeleton />
      </div>
    );
  }

  if (loadingState === LoadingState.ERROR) {
    return (
      <div className="mt-2 text-sm text-orange-600">
        An error occurred while loading the transcript. Please refresh and try
        again.
      </div>
    );
  }

  if (timelineEvents.length === 0) {
    return (
      <div className="mt-2 text-sm text-muted-foreground">
        The transcript will be available once the candidate starts or completes
        their interview.
      </div>
    );
  }

  const renderCallMarker = (
    type: "call_start" | "call_end",
    call: CompletedInterviewCallMetadata,
  ) => {
    const isStart = type === "call_start";
    const timestamp = new Date(isStart ? call.start_time : call.end_time);
    const formattedTime = timestamp.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });

    return (
      <div>
        <div className="flex items-center justify-center py-2 text-sm">
          <div className="flex items-center space-x-2">
            <IconPhone
              className={twMerge(
                "h-4 w-4",
                isStart ? "text-green-500" : "text-red-500",
              )}
            />
            <span>{isStart ? "Call started" : "Call ended"}</span>
            <span className="text-muted-foreground">{formattedTime}</span>
          </div>
        </div>
        {isStart && (
          <div className="my-2">
            {(() => {
              const videoUrl = getVideoUrl(call);
              const audioUrl = getAudioRecordingUrl(call);
              const hasPlayableAudio = !!audioUrl && !failedUrls.has(audioUrl);
              const hasPlayableVideo = !!videoUrl && !failedUrls.has(videoUrl);
              const preferAudioOnly = audioOnlyCallIds.has(call.id);
              const showVideo =
                hasPlayableVideo && !(preferAudioOnly && hasPlayableAudio);

              if (!showVideo && !hasPlayableAudio) return null;

              return (
                <div className="flex flex-col items-start gap-1">
                  {showVideo ? (
                    <video
                      ref={(el) => setAudioRef(call.id, el)}
                      controls
                      src={videoUrl}
                      className="w-full max-w-[300px]"
                      onError={() => handleAudioError(videoUrl)}
                    >
                      Your browser does not support the video element.
                    </video>
                  ) : (
                    <audio
                      ref={(el) => setAudioRef(call.id, el)}
                      controls
                      src={audioUrl!}
                      onError={() => handleAudioError(audioUrl!)}
                    >
                      Your browser does not support the audio element.
                    </audio>
                  )}
                  {hasPlayableVideo && hasPlayableAudio && (
                    <Button
                      variant={ButtonVariant.Tertiary}
                      onClick={() => toggleAudioOnly(call.id)}
                      className="text-xs font-normal text-muted-foreground underline hover:text-gray-700"
                    >
                      {preferAudioOnly
                        ? "Switch back to video"
                        : "Switch to audio only"}
                    </Button>
                  )}
                </div>
              );
            })()}
          </div>
        )}
        {!isStart && <div className="my-2 border-b border-border-light" />}
      </div>
    );
  };

  const renderSmsMarker = (activity: Activity, isFirstSms: boolean) => {
    const timestamp = new Date(activity.created_at);
    const formattedTime = timestamp.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });
    return (
      <div className="flex items-center justify-between py-2 text-sm">
        <div className="flex items-center space-x-2">
          <IconMessageCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span>
            {isFirstSms ? "Initial SMS invitation sent" : "SMS reminder sent"}
          </span>
          <span className="text-muted-foreground">{formattedTime}</span>
        </div>
      </div>
    );
  };

  const renderSectionMarker = (section: AdminApiInterviewSectionResult) => (
    <Label size={Size.SMALL} className="mt-4 mb-2">
      {getInterviewSectionTitle(section.settings)}
    </Label>
  );

  return (
    <>
      {interviewInsights?.unanswered_candidate_questions && (
        <UnansweredQuestionsBanner
          unansweredQuestions={interviewInsights.unanswered_candidate_questions}
          jumpToFragmentByText={jumpToFragmentByText}
        />
      )}
      <div className="mb-4 flex items-center gap-4">
        {hasRecordings && (
          <div className="grid w-[150px]">
            <SimpleSelect
              label="Playback speed"
              isLabelHidden
              options={speedOptions}
              value={playbackSpeed}
              onChange={setPlaybackSpeed}
              placeholder="Select speed"
            />
          </div>
        )}
        <div className="relative inline-block text-left">
          <MenuTrigger>
            <Button variant={ButtonVariant.Secondary}>
              {selectedLanguage
                ? `Translated to ${LANGUAGE_DISPLAY_NAMES[selectedLanguage]}`
                : "Translate to..."}
            </Button>
            <DropdownMenu
              items={translateDropdownItems.map((item) => ({
                label: item.label,
                onClick: () => {
                  setSelectedLanguage(item.value);
                  onClickTranslate?.(item.value);
                },
              }))}
            />
          </MenuTrigger>
        </div>
      </div>
      {timelineEvents.map((event, i) => {
        if (event.type === "transcript") {
          const fragment = event.data;

          // Display the original transcript or a translated version if selected
          const displayText =
            selectedLanguage &&
            fragment.text_transcript_translations?.[selectedLanguage]
              ? fragment.text_transcript_translations[selectedLanguage]
              : fragment.text_transcript;

          return (
            <div
              key={fragment.id}
              ref={(el) => {
                if (el) {
                  transcriptFragmentRefs.current[fragment.id] = el;
                }
              }}
              className={twMerge(
                "cursor-pointer text-sm text-foreground",
                fragment.is_dynamic_question ? "pl-8" : "",
              )}
              style={{ scrollMarginTop: STICKY_HEADER_HEIGHT }}
              onClick={() => playAudioAtFragment(fragment)}
            >
              {fragment.question ? (
                <div
                  className={twMerge(
                    "mb-2 text-foreground",
                    i !== 0 ? "mt-4" : "",
                  )}
                >
                  {fragment.question}
                </div>
              ) : null}
              <TranscriptFragmentText
                text={displayText || null}
                isHighlighted={fragment.id === highlightedTranscriptFragment}
                onClickShare={() => onClickShare(fragment)}
                role={fragment.role}
              />
            </div>
          );
        } else if (event.type === "call_start" || event.type === "call_end") {
          return (
            <div key={`${event.type}-${event.timestamp}`}>
              {renderCallMarker(event.type, event.data)}
            </div>
          );
        } else if (event.type === "section_start") {
          return (
            <div key={`${event.type}-${event.timestamp}`}>
              {renderSectionMarker(event.data)}
            </div>
          );
        } else if (event.type === "sms_reminder") {
          return (
            <div key={`${event.type}-${event.timestamp}`}>
              {renderSmsMarker(event.data, event.isFirstSms)}
            </div>
          );
        } else {
          return null;
        }
      })}
    </>
  );
};
